/**
 * Locale-aware formatters
 * Utilities for formatting dates, times, numbers, and currency
 */

import type { DateFormatType, SupportedLocale } from './config'

/**
 * Format date using user's locale and preferences
 */
export const formatDate = (
  date: Date,
  locale: SupportedLocale,
  options?: Intl.DateTimeFormatOptions,
): string => {
  return date.toLocaleDateString(locale, options)
}

/**
 * Format time using user's locale and preferences
 */
export const formatTime = (
  date: Date,
  locale: SupportedLocale,
  is24Hour: boolean,
  options?: Intl.DateTimeFormatOptions,
): string => {
  const hourCycle = is24Hour ? 'h23' : 'h12'
  return date.toLocaleTimeString(locale, {
    ...options,
    hourCycle,
  })
}

/**
 * Format date and time together
 */
export const formatDateTime = (
  date: Date,
  locale: SupportedLocale,
  is24Hour: boolean,
  options?: Intl.DateTimeFormatOptions,
): string => {
  const hourCycle = is24Hour ? 'h23' : 'h12'
  return date.toLocaleString(locale, {
    ...options,
    hourCycle,
  })
}

/**
 * Get Luxon format pattern from date format type
 */
export const getLuxonDateFormat = (
  dateFormat: DateFormatType,
  includeYear = true,
): string => {
  switch (dateFormat) {
    case 'MM/dd/yyyy':
      return includeYear ? 'MMM d, yyyy' : 'MMM d'
    case 'dd/MM/yyyy':
      return includeYear ? 'd MMM yyyy' : 'd MMM'
    case 'yyyy-MM-dd':
      return includeYear ? 'yyyy MMM d' : 'MMM d'
    default:
      return includeYear ? 'MMM d, yyyy' : 'MMM d'
  }
}

/**
 * Get Luxon time format pattern
 */
export const getLuxonTimeFormat = (is24Hour: boolean): string => {
  return is24Hour ? 'HH:mm' : 'h:mm a'
}

/**
 * Format currency using user's locale
 */
export const formatCurrency = (
  amount: number,
  locale: SupportedLocale,
  currency = 'USD',
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount)
}

/**
 * Format number using user's locale
 */
export const formatNumber = (value: number, locale: SupportedLocale): string => {
  return new Intl.NumberFormat(locale).format(value)
}

/**
 * Format relative time (e.g., "2 days ago", "in 3 hours")
 */
export const formatRelativeTime = (
  date: Date,
  locale: SupportedLocale,
  baseDate: Date = new Date(),
): string => {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

  const diff = date.getTime() - baseDate.getTime()
  const sign = diff < 0 ? -1 : 1
  const absMs = Math.abs(diff)
  const seconds = Math.floor(absMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  if (years >= 1) {
    return rtf.format(sign * years, 'year')
  }
  if (months >= 1) {
    return rtf.format(sign * months, 'month')
  }
  if (weeks >= 1) {
    return rtf.format(sign * weeks, 'week')
  }
  if (days >= 1) {
    return rtf.format(sign * days, 'day')
  }
  if (hours >= 1) {
    return rtf.format(sign * hours, 'hour')
  }
  if (minutes >= 1) {
    return rtf.format(sign * minutes, 'minute')
  }
  return rtf.format(sign * seconds, 'second')
}

/**
 * Check if two dates are the same day
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

/**
 * Format date range label
 */
export const formatDateRange = (
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
  locale: SupportedLocale,
  is24Hour: boolean,
  isAllDayFlag?: boolean,
): string => {
  if (!startDate && !endDate) {
    return ''
  }

  if (startDate && endDate) {
    const sameDay = isSameDay(startDate, endDate)

    // Prefer the explicit isAllDay flag; fall back to legacy time-based detection
    // (00:00 - 23:59) so existing callers continue to work.
    const isAllDay =
      isAllDayFlag ??
      (startDate.getHours() === 0 &&
        startDate.getMinutes() === 0 &&
        endDate.getHours() === 23 &&
        endDate.getMinutes() === 59)

    if (sameDay) {
      const datePart = formatDate(startDate, locale, {
        month: 'short',
        day: 'numeric',
      })

      if (isAllDay) {
        return datePart
      }

      const startTime = formatTime(
        startDate,
        locale,
        is24Hour,
        { hour: 'numeric', minute: '2-digit' },
      )
      const endTime = formatTime(
        endDate,
        locale,
        is24Hour,
        { hour: 'numeric', minute: '2-digit' },
      )
      return `${datePart}, ${startTime} - ${endTime}`
    }

    // Different days
    const startPart = formatDate(startDate, locale, {
      month: 'short',
      day: 'numeric',
    })
    const endPart = formatDate(endDate, locale, {
      month: 'short',
      day: 'numeric',
    })

    if (isAllDay) {
      return `${startPart} - ${endPart}`
    }

    const startTime = formatTime(
      startDate,
      locale,
      is24Hour,
      { hour: 'numeric', minute: '2-digit' },
    )
    const endTime = formatTime(
      endDate,
      locale,
      is24Hour,
      { hour: 'numeric', minute: '2-digit' },
    )
    return `${startPart} ${startTime} - ${endPart} ${endTime}`
  }

  // Only one date provided
  const date = (startDate || endDate)!
  return formatDate(date, locale, {
    month: 'short',
    day: 'numeric',
  })
}
